import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { CalendarDate, ChevronDown } from "@untitledui/icons"

import { BookInline } from "./book-inline"
import type { TaskRow } from "@/lib/api/tasks"
import { cx } from "@/lib/utils/cx"

// The calendar is on the desk, not in a drawer. On a call — "can I come
// Thursday?" — she looks right, picks the slot, presses the one green
// button. The pane follows the visit card's step: a no-show becomes "book
// again".

export type BookMode = "book" | "rebook"

interface BookPaneProps {
  row: TaskRow
  teamId: number
  mode: BookMode
  /** The visit card hasn't said whether they came yet — the pane waits. */
  waiting?: boolean
  beforeBook?: () => Promise<void>
  onBooked: (appointmentId: number, startsAt: string) => void
  /** Below lg the pane is a bar; open it by default where booking IS the task. */
  defaultOpen?: boolean
}

const TITLE: Record<BookMode, (first: string) => string> = {
  book: () => "Book a visit",
  rebook: (first) => `Book ${first} again`,
}

export function BookPane({ row, teamId, mode, waiting, beforeBook, onBooked, defaultOpen = false }: BookPaneProps) {
  const [open, setOpen] = useState(defaultOpen)
  const reduce = useReducedMotion()
  const first = row.customerName.split(" ")[0] || row.customerName
  const title = TITLE[mode](first)

  const body = waiting ? (
    <p className="text-[13px] text-zinc-500">First say whether {first} came in.</p>
  ) : (
    <BookInline
      key={mode}
      teamId={teamId}
      estimateId={row.estimateId}
      customerName={row.customerName}
      compact={false}
      beforeBook={beforeBook}
      onBooked={onBooked}
    />
  )

  return (
    <div>
      {/* Desktop: a heading. */}
      <div className="hidden lg:flex items-center gap-2 mb-3">
        <CalendarDate className="size-4 text-emerald-600" aria-hidden />
        <h3 className="text-[15px] font-semibold text-zinc-900">{title}</h3>
      </div>
      <div className="hidden lg:block">{body}</div>

      {/* Phone: a bar. Visible, one tap. */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full h-12 px-4 rounded-lg border border-zinc-300 bg-white inline-flex items-center justify-between text-[14px] font-semibold text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20 focus-visible:ring-offset-1"
        >
          <span className="inline-flex items-center gap-2">
            <CalendarDate className="size-4 text-emerald-600" aria-hidden />
            {title}
          </span>
          <ChevronDown className={cx("size-4 text-zinc-500 transition-transform duration-200 ease-out-quart motion-reduce:transition-none", open && "rotate-180")} aria-hidden />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
              animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
              exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: reduce ? 0.12 : 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-3">{body}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
